import { ref } from '@vue/composition-api'
import PrintTicketApi from '@/api/waiter/PrintTicket'
import useCurrentData from './useCurrentData'
import useSdpPrinterHealth, { formatPolledAt } from './useSdpPrinterHealth'
import { canExecutePrintOnThisDevice } from './useReceiptPrinterDeviceSetting'

/**
 * レシート系伝票の SDP 印刷（印刷依頼API → チケット状態ポーリング → 成否トースト）。
 *
 * 店舗の receiptSdpEnabled が有効な場合に、従来のブラウザ→プリンタ直結（ePOS SDK）の
 * 代わりにこの composable を使う。印刷データの組立はサーバー側で行われるため、
 * 呼び出し側は伝票種別と素材の参照（tableId / receiptId）・手入力値のみを渡す。
 *
 * 使い方:
 *   const { isSdpEnabled, requestPrint } = useSdpSlipPrint({ toast: vm.$toast })
 *
 *   const onPrint = async () => {
 *     if (isSdpEnabled()) {
 *       await requestPrint({ ticketType: 'accounting', tableId: props.tableId })
 *       return
 *     }
 *     // 従来のブラウザ直結印刷
 *   }
 */

// プリンタのSDPポーリングは数秒間隔のため、1.5秒間隔・最大30秒で状態を確認する
const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 30000

const ERROR_CODE_MESSAGES = {
  EPTR_REC_EMPTY: '用紙切れ',
  EPTR_COVER_OPEN: 'カバーオープン',
  EPTR_AUTOMATICAL: '自動復帰エラー',
}

const sleep = ms => new Promise(resolve => { setTimeout(resolve, ms) })

export default function useSdpSlipPrint({ toast } = {}) {
  const { currentClub } = useCurrentData()
  const { fetchHealth, stalePrinters, formatSince } = useSdpPrinterHealth()
  const isPrinting = ref(false)

  // タイムアウト時の案内文。プリンタのポーリング途絶が原因のことが多い
  // (2026-08-22実障害)ため、最終通信時刻と再起動の案内を含める
  const buildTimeoutMessage = async () => {
    const base = '印刷を確認できませんでした。'
    try {
      await fetchHealth()
      if (stalePrinters.value.length > 0) {
        const parts = stalePrinters.value.map(pr => {
          const since = formatSince(pr.lastPolledAt)
          const at = formatPolledAt(pr.lastPolledAt)

          return since ? `${pr.name}(最終ポーリング成功: ${at}・${since})` : `${pr.name}(ポーリング記録なし)`
        })

        return `${base}プリンタがサーバーと通信できていません: ${parts.join(' / ')}。プリンタの電源を入れ直してください`
      }
    } catch {
      // 死活取得に失敗しても従来文言で案内する
    }

    return `${base}出てこない場合はプリンタの電源を入れ直すか、プリンタ設定の印刷チケット一覧を確認してください`
  }

  // 店舗がレシートSDP有効か（印刷経路の分岐に使う）
  const isSdpEnabled = () => !!currentClub.value?.receiptSdpEnabled

  // チケット状態をポーリングし、結果をトースト表示する
  async function watchTicket(ticketId) {
    const startedAt = Date.now()
    while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_INTERVAL_MS)
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await PrintTicketApi.getPrintTicket(ticketId)
        if (data.status === 'completed') {
          if (toast) toast.success('印刷しました')

          return { status: 'completed', ticketId, errorCode: null }
        }
        if (data.status === 'failed') {
          const reason = ERROR_CODE_MESSAGES[data.errorCode] || data.errorCode || '不明なエラー'
          if (toast) toast.error(`印刷に失敗しました（${reason}）。プリンタ設定の印刷チケット一覧から再送できます`)

          return { status: 'failed', ticketId, errorCode: data.errorCode }
        }
      } catch {
        // 一時的な通信エラーはポーリング継続
      }
    }

    if (toast) toast.warning(await buildTimeoutMessage())

    return { status: 'timeout', ticketId, errorCode: null }
  }

  /**
   * 印刷依頼してチケット状態を見届ける。
   * inputs はサーバー仕様に合わせて snake_case のキーで渡すこと
   * （例: { customer_name, receipt_note, issue_date, amount, with_order_details }）。
   *
   * @returns {Promise<{ status: string, ticketId: number|null, errorCode: string|null }>}
   */
  async function requestPrint({
    ticketType, tableId, receiptId, inputs, watch = true,
  }) {
    // 「この端末で印刷を使用する」OFF の端末からは依頼しない（従来挙動と同じガード）
    if (!canExecutePrintOnThisDevice()) return { status: 'skipped', ticketId: null, errorCode: null }

    isPrinting.value = true
    try {
      const res = await PrintTicketApi.createPrintTicket({
        ticket_type: ticketType,
        table_id: tableId,
        receipt_id: receiptId,
        inputs,
      })
      const ticketId = res.data.id

      // ドロワーキックのような印字を伴わない依頼は、結果がその場で分かるため
      // 完了ポーリング・成功トーストを省略できる
      if (!watch) return { status: 'requested', ticketId, errorCode: null }

      return await watchTicket(ticketId)
    } catch (e) {
      const message = e?.response?.data?.error || '印刷依頼に失敗しました'
      if (toast) toast.error(message)

      return { status: 'request_failed', ticketId: null, errorCode: null }
    } finally {
      isPrinting.value = false
    }
  }

  return {
    isPrinting,
    isSdpEnabled,
    requestPrint,
  }
}

/**
 * 概算伝票用: 注文画面の概算ターゲット（未確定注文）を印刷依頼APIの
 * estimation_targets（snake_case）へ変換する。
 * 未確定注文はサーバーに存在しないため、ここだけフロント計算値を渡す。
 *
 * @param {Array} targets  Order.vue の estimatedSlipOptions.estimationTargets
 *   （{ estimatedTotal, estimatedTax, vendingHistoriesTemp } の配列）
 * @param {Object} ctx     { vendibles, recipients, courseAlias, nominationAlias }
 */
export function buildEstimationTargetInputs(targets, {
  vendibles = [], recipients = [], courseAlias, nominationAlias,
} = {}) {
  const typeLabels = {
    Course: courseAlias || 'セット',
    Nomination: nominationAlias || '指名',
    Item: 'アイテム',
  }

  return (targets || []).map(target => {
    const groups = { Course: [], Nomination: [], Item: [] }
    let subtotal = 0;
    (target.vendingHistoriesTemp || []).forEach(temp => {
      const {
        vendibleId, vendibleType, sellingPrice, isNonExistVendible, vendibleRecipientsAttributes,
      } = temp.value
      const quantity = temp.size || 1
      const price = sellingPrice || 0

      // 商品名の解決（micro_app formatVendingHistoriesTempToOrderDetails と同じ）
      let name = temp.value.vendibleName || ''
      if (!isNonExistVendible && vendibleId && vendibleType) {
        const vendible = vendibles.find(v => +v.id === +vendibleId && v.attributes.vendibleType === vendibleType)
        name = vendible?.attributes?.name || name
      }

      const recipientName = (vendibleRecipientsAttributes || [])
        .map(r => recipients.find(u => +u.id === r.userId)?.attributes?.name)
        .filter(Boolean)
        .join(', ')

      const type = (!vendibleType || isNonExistVendible || !groups[vendibleType]) ? 'Item' : vendibleType
      groups[type].push({
        name,
        quantity,
        selling_price: price,
        amount: price * quantity,
        recipient_name: recipientName || null,
      })
      subtotal += price * quantity
    })

    return {
      order_details: Object.entries(groups).map(([type, records]) => ({ label: typeLabels[type], records })),
      subtotal,
      tax: target.estimatedTax || 0,
      total: target.estimatedTotal || 0,
    }
  })
}
